/**
 * Maps Clerk error codes to i18n translation keys.
 *
 * Clerk returns error messages in English regardless of the UI language.
 * This utility translates Clerk error codes into our own i18n keys so
 * that error messages respect the user's selected locale.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslateFn = (key: any, params?: Record<string, string | number>) => string;

const CLERK_ERROR_CODE_MAP: Record<string, string> = {
  form_identifier_not_found: 'auth.clerkErrors.identifierNotFound',
  form_password_incorrect: 'auth.clerkErrors.passwordIncorrect',
  form_password_pwned: 'auth.clerkErrors.passwordPwned',
  form_identifier_exists: 'auth.clerkErrors.identifierExists',
  form_code_incorrect: 'auth.clerkErrors.codeIncorrect',
  form_password_length_too_short: 'auth.clerkErrors.passwordTooShort',
  form_password_not_strong_enough: 'auth.clerkErrors.passwordNotStrongEnough',
  form_password_size_in_bytes_exceeded: 'auth.clerkErrors.passwordTooLong',
  form_param_format_invalid: 'auth.clerkErrors.invalidFormat',
  too_many_attempts: 'auth.clerkErrors.tooManyAttempts',
  verification_expired: 'auth.clerkErrors.verificationExpired',
  form_password_validation_failed: 'auth.clerkErrors.passwordValidationFailed',
  verification_failed: 'auth.clerkErrors.verificationFailed',
  form_conditional_param_value_disallowed: 'auth.clerkErrors.invalidValue',
  strategy_for_user_invalid: 'auth.clerkErrors.strategyInvalid',
};

interface ClerkError {
  errors?: { message: string; code?: string }[];
}

/**
 * Extracts a translated error message from a Clerk error object.
 * Falls back to the generic error translation if the code is unknown.
 */
export function getClerkErrorMessage(err: unknown, t: TranslateFn): string {
  const clerkError = err as ClerkError;
  const firstError = clerkError.errors?.[0];

  if (!firstError) {
    return t('auth.genericError');
  }

  const code = firstError.code;
  if (code && code in CLERK_ERROR_CODE_MAP) {
    return t(CLERK_ERROR_CODE_MAP[code]);
  }

  // Unknown code — use generic error instead of raw English message
  return t('auth.genericError');
}
